<template>
	<div v-if="hasEditPermissions">
		<AppDropdownMenu :items="localMenuItems" content-class="men" min-width="200" absolute left bottom>
			<template #activator>
				<v-btn tabindex="-1" class="flex-centerize" width="24" height="26" icon fab>
					<SIcon class="menu-button" name="menu-button" width="20" />
				</v-btn>
			</template>
			<template #list-item="{ item }">
				<div class="menu-item py-1">
					{{ item.text }}
				</div>
			</template>
		</AppDropdownMenu>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import { editNode, isEndRun } from '@/store/modules/selectedNode/getters';
import { canEditWorkflow } from '@/store/modules/workflows/getters';
import { setContextualDataState } from '@/store/modules/selectedWorkflow/actions';
import { setEditorAccordionMenuItems, setIsEditorAccordionMenuOpen } from '@/store/modules/blockEditor/actions';
import { setIsChangingAction } from '@/store/modules/blockEditor/actions';

import AppService from '@/utils/AppService';
import AppDialogManagerService from '../dialogs/AppDialogManager/app-dialog-manager-service';
import BlockEditorService from './block-editor-service';

export default {
	name: 'StepDropdownMenu',
	props: {
		showDeleteOnly: { type: Boolean },
		isStepLockedForMe: { type: Boolean },
		hideProfileSelectionOption: { type: Boolean },
		isContextualStep: { type: Boolean },
	},
	data() {
		return {
			deleteItem: { text: 'Delete step', handler: this.deleteStep },
			skipItem: { text: 'Skip step', handler: this.skipStep },
			changeIntegrationItem: { text: 'Change Integration', handler: this.changeIntegration, changeAction: true },
		};
	},
	computed: {
		...mapGettersShorthand({ editNode, isEndRun }),
		...mapGetters(['apps/getService', canEditWorkflow.fullPath]),
		hideChangeActions() {
			return this.isContextualStep;
		},
		hasEditPermissions() {
			return this[canEditWorkflow.fullPath](this.editNode.workflow);
		},
		localMenuItems() {
			const { isContextualStep, isEndRun, changeIntegrationItem } = this;

			if (this.showDeleteOnly && !isEndRun) {
				return [
					{
						...this.deleteItem,
						onClick: () => this.onItemClick(this.deleteItem),
					},
				];
			}
			const baseItems = [...BlockEditorService.getStepDropdownMenuItems(isContextualStep), isEndRun ? null : changeIntegrationItem]
				.filter((item) => !!item)
				.filter((i) => {
					if (i.isProfileSelection) {
						return !this.hideProfileSelectionOption && this.profileSelectionNeeded;
					} else if (i.changeAction) {
						return !this.hideChangeActions && !this.isStepLockedForMe;
					}
					return true;
				});

			const belowDividerItems = [isContextualStep && this.skipItem, !isEndRun && this.deleteItem].filter((i) => !!i);
			if (baseItems.length > 0 && belowDividerItems.length > 0) {
				baseItems.push({ divider: true });
			}
			baseItems.push(...belowDividerItems);

			return baseItems.map((i) => ({
				...i,
				onClick: () => this.onItemClick(i),
			}));
		},
		selectedApp() {
			return this['apps/getService'](this.editNode.service) || {};
		},
		profileSelectionNeeded() {
			return this.editNode.action && AppService.doesRequiresAuth(this.selectedApp);
		},
	},
	methods: {
		...mapActionsShorthand({ setContextualDataState, setEditorAccordionMenuItems, setIsEditorAccordionMenuOpen, setIsChangingAction }),
		showSelectAppScreen() {
			this.$emit('showSelectAppScreen');
		},
		deleteStep() {
			AppDialogManagerService.openDeleteBlockDialog({ block: this.editNode });
		},
		changeIntegration() {
			this.$emit('showSelectAppScreen');
		},
		onItemClick(item) {
			if (item.handler) {
				item.handler();
			} else if (item.id === 'change_action') {
				this.setIsChangingAction(true);
				setTimeout(() => {
					this.onDropdownMenuItemClick(item);
				}, 200);
			} else {
				this.onDropdownMenuItemClick(item);
			}
		},
		skipStep() {
			// supposed to happen only for add-on feature, set contextual data state for the add-on as false
			this.setContextualDataState({ state: false });
		},

		onDropdownMenuItemClick(item) {
			this.setEditorAccordionMenuItems(item);
			this.setIsEditorAccordionMenuOpen(true);
		},
	},
};
</script>

<style scoped lang="scss">
.menu-item {
	font-size: 14px;
	font-weight: normal;
	color: var(--primary-font-color);
}
.top-border {
	width: 100%;
	border-top: 1px solid var(--border-color);
}
.menu-button {
	margin-top: 4px;
	margin-right: 5px;
}
</style>
